import { Button } from "$components/button/button";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { makeStyles } from "$components/mui-styles";
import { toast } from "$global-providers/toast-provider";
import dayjs from "$routes/_lib/dayjs";
import type { NewCardsSendPinPageState } from "$routes/new/card/_lib/send-pin/SendPinPage";
import type { NewCardsShowPinPageState } from "$routes/new/card/_lib/show-pin/ShowPinPage";
import {
  linkToNewCardsSendPin,
  linkToNewCardsShowPin,
} from "$routes/new/links";
import { gql } from "@apollo/client";
import { Box, Checkbox, styled, Typography } from "@material-ui/core";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import type { ContactPoint } from "elephant-web/gql";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useReissuePinMutation } from "./__generated__/reissue-pin-modal.gql";

const useStyles = makeStyles((theme) => ({
  body: {
    paddingTop: "16px",
    paddingBottom: "0px",
  },
  headerText: {
    fontWeight: "bold",
  },
  headerWrapper: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    paddingBottom: "16px",
  },
  headerComponent: {
    paddingBottom: "0",
    background: "white",
  },
  contentWrapper: {
    width: "100%",
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  warningIcon: {
    color: theme.palette.warning.main,
    height: 50,
    width: 50,
  },
  question: {
    display: "inline-block",
    minWidth: "250px",
    width: "250px",
  },
  questionsWrapper: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "8px",
    fontFamily: theme.typography.fontFamily,
  },
  questionsHeading: {
    marginBottom: "16px",
  },
}));

const WarningBreakout = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2),
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
}));

const Container = styled(DialogContainer)(({ theme }) => ({
  "& .MuiDialog-paper": {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

const Body = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const CHECKBOX_ID = "reissuepin-confirmation";

const SecurityQuestion: FC<{
  question: string;
  answer: string;
  classes: { question: string };
}> = ({ question, answer, classes }) => (
  <span>
    <span className={classes.question}>
      {question}
      {"? "}
    </span>
    {answer}
  </span>
);

gql`
  mutation reissuePin($patientUuid: String!) {
    createPin(patientUuid: $patientUuid) {
      pin
    }
  }
`;

export const ReissuePinModal = ({
  open,
  onClose,
  patient,
}: {
  onClose: () => void;
  open: boolean;
  patient: {
    uuid: string;
    contactPoints: readonly ContactPoint[];
    fullName: string;
    dob: string;
  };
}) => {
  const { formatMessage } = useIntl();
  const [confirmed, setConfirmed] = useState(false);
  const classes = useStyles();
  const navigate = useNavigate();

  const [createPin] = useReissuePinMutation({
    variables: { patientUuid: patient.uuid },
    onCompleted: ({ createPin: { pin } }) => {
      onClose();
      const hasPhone = patient.contactPoints.find(
        (c) => c.system === "phone" && /(personal|shared)/.test(c.ownership),
      );

      if (hasPhone) {
        const nextState: NewCardsSendPinPageState = {
          phoneNumber: hasPhone.value,
          pin,
          patientId: patient.uuid,
        };
        navigate(linkToNewCardsSendPin(), {
          state: nextState,
        });
      } else {
        const nextState: NewCardsShowPinPageState = {
          pin,
          patientId: patient.uuid,
        };
        navigate(linkToNewCardsShowPin(), {
          state: nextState,
        });
      }
    },
    onError: (e) =>
      toast.error(
        formatMessage(
          {
            id: "reissuePinModal.serverError",
            defaultMessage:
              "There was an issue, please try again later: {message}",
          },
          { message: e.message },
        ),
      ),
  });

  const phone = patient.contactPoints[0]?.value
    ? patient.contactPoints[0].value
    : "-";

  return (
    <Container
      open={open}
      onClose={onClose}
      data-testid="patientConsentModalContainer"
    >
      <DialogHeader
        title={formatMessage({
          id: "patient.reissuepin.title",
          defaultMessage: "Identity Check",
        })}
      />
      <Body>
        <WarningBreakout>
          <ErrorOutlineIcon className={classes.warningIcon} />
          <Typography color="inherit">
            <FormattedMessage
              id="patient.reissuepin.confirm"
              defaultMessage="Please check patient's identity. If the identity can not be confirmed, do not continue."
            />
          </Typography>
        </WarningBreakout>
        <div className={classes.questionsWrapper}>
          <span className={classes.questionsHeading}>
            {"Security questions:"}
          </span>
          <SecurityQuestion
            question="1. What is your full name"
            answer={patient.fullName}
            classes={classes}
          />
          <SecurityQuestion
            question="2. What is your phone number"
            answer={phone}
            classes={classes}
          />
          <SecurityQuestion
            question="3. What is your date of birth"
            answer={dayjs(patient.dob).format("DD/MM/YYYY")}
            classes={classes}
          />
        </div>
        <label
          htmlFor={CHECKBOX_ID}
          data-testid="header-reissuepin-checkbox-label"
        >
          <Box display="flex" alignItems="center">
            <Box mr={2}>
              <Checkbox
                autoFocus
                color="primary"
                id={CHECKBOX_ID}
                value={confirmed}
                onChange={(e) => {
                  setConfirmed(e.target.checked);
                }}
                inputProps={{
                  "data-testid": "patientConsentCheckbox",
                }}
                data-testid="header-reissuepin-checkbox"
              />
            </Box>
            <Typography variant="body2">
              <FormattedMessage
                id="patient.reissuepin.checkbox"
                defaultMessage="I confirm the patient's identity"
              />
            </Typography>
          </Box>
        </label>
      </Body>
      <DialogFooter>
        <Button
          color="secondary"
          onClick={onClose}
          data-testid="patientConsentCancelBtn"
        >
          <FormattedMessage
            id="patient.reissuepin.cancel"
            defaultMessage="Cancel"
          />
        </Button>
        <Button
          color="primary"
          onClick={() => createPin()}
          disabled={!confirmed}
          data-testid="header-reissuepin-confirm"
        >
          <FormattedMessage
            id="patient.reissuepin.confirmationBtn"
            defaultMessage="Confirm"
          />
        </Button>
      </DialogFooter>
    </Container>
  );
};
